import { memo, useEffect, useState } from 'react';

import { Flex, Select, Text } from '@raystack/apsara';
import clsx from 'clsx';
import * as _ from 'lodash';

import { ScrollView } from '@src/components/Custom/ScrollView';
import { LandscapeThumbnail } from '@src/components/Custom/Thumbnails';
import { For } from '@src/hoc';
import {
  MODELWISE_PARAMETER_DICT,
  PARAMETER_MODELS
} from '@src/utils/constants/MODE_INFERENCE_DEFAULTS';

import styles from './styles.module.css';

type parameter = {
  title: string;
  name: string;
  src: string;
};

interface ItemProps {
  children: JSX.Element | string;
  src: string;
  isPlaceholder?: boolean;
}

interface ParamterInterface {
  assetType: string;
  value: string;
  onValueChange: (value: string) => void;
}

const Item = ({ children, src, isPlaceholder }: ItemProps) => {
  return (
    <Flex gap='small' align='center'>
      <LandscapeThumbnail
        src={src}
        className={clsx(isPlaceholder && styles.placeholderImg)}
      />

      <div>{children}</div>
    </Flex>
  );
};

export const ParameterSelector = memo(
  ({ assetType = '', value = '', onValueChange }: ParamterInterface) => {
    const [currentParams, setCurrentParams] = useState({});

    useEffect(() => {
      PARAMETER_MODELS.forEach(model => {
        if (model === assetType && _.has(MODELWISE_PARAMETER_DICT, assetType)) {
          setCurrentParams(MODELWISE_PARAMETER_DICT[assetType] || {});
        }
      });
    }, [assetType]);

    return (
      <Flex
        direction='column'
        align='start'
        gap='extra-small'
        style={{ width: '100%' }}
      >
        <Text
          weight={500}
          size={1}
          style={{ color: 'var(--rs-color-foreground-base-secondary)' }}
        >
          Parameter
        </Text>
        <Select value={value} onValueChange={onValueChange}>
          <Select.Trigger className={styles.triggerStyle}>
            <Select.Value
              placeholder={
                <Item src={''} isPlaceholder>
                  Choose output parameter
                </Item>
              }
            />
          </Select.Trigger>
          <Select.Content>
            <ScrollView classes={{ root: styles.scrollRoot }}>
              <For each={Object.keys(currentParams)}>
                {param_name => {
                  const paramter =
                    (_.has(currentParams, param_name) &&
                      currentParams[param_name]) ||
                    ({} as parameter);

                  const { src = '', title = '' } = paramter;
                  return (
                    <Select.Item value={param_name} key={param_name}>
                      <Item src={src}>
                        <Text weight={500} size={4}>
                          {title}
                        </Text>
                      </Item>
                    </Select.Item>
                  );
                }}
              </For>
            </ScrollView>
          </Select.Content>
        </Select>
      </Flex>
    );
  }
);

ParameterSelector.displayName = 'ParameterSelector';
